import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Stack, Chip, IconButton,
  Accordion, AccordionSummary, AccordionDetails,
  CircularProgress, Alert,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Star as TargetIcon,
  ExpandMore as ExpandMoreIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as PushIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useFileWebSocket } from '../../hooks/useFileWebSocket';
import { useObservationSelection } from '../../hooks/useObservationSelection';
import SelectionBar from './SelectionBar';
import FileGrid from './FileGrid';
import { computeGroupStats, sumFileBytes } from '../../utils/fileStats';
import { formatSize } from '../../utils/fileUtils';
import { getAccessToken } from '../../services/authStorage';

const fileIdsOf = (files) => (files || []).map((f) => f._id || f.file_id); // server bulk-delete matches _id

const API_BASE_URL = '/api/v1';

const nestedAccordionSx = {
  '&:before': { display: 'none' },
  boxShadow: 'none',
  borderTop: '1px solid',
  borderColor: 'divider',
};

const groupFilesByTarget = (files) => {
  const byTarget = {};
  (files || []).forEach(f => {
    const t = f.target_name || 'Unknown Target';
    if (!byTarget[t]) byTarget[t] = [];
    byTarget[t].push(f);
  });
  return byTarget;
};

const DateGroupList = ({ searchTerm, onDownloadGroup, onPushGroup, onPreview, getDownloadUrl, getCalibratedDownloadUrl, downloadCalibrated, onCalibrateError, onStatsLoaded, onDelete, canDelete, onBulkDelete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [expandedTargets, setExpandedTargets] = useState(new Set());
  const [loadedFiles, setLoadedFiles] = useState({});
  const [masterCoverage, setMasterCoverage] = useState({});
  const [loadingFiles, setLoadingFiles] = useState(new Set());
  const loadedFilesRef = useRef(loadedFiles);
  loadedFilesRef.current = loadedFiles;
  const selectionEnabled = !!onBulkDelete;
  const selection = useObservationSelection();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/user-files/by-date`, {
        headers: { Authorization: `Bearer ${token}` },
        params: searchTerm ? { search: searchTerm } : {}
      });
      if (response.data.success) {
        setData(response.data.data);
        setLoadedFiles({});
        if (onStatsLoaded && response.data.data?.groups) {
          onStatsLoaded(computeGroupStats(response.data.data.groups, { observations: response.data.data.total_observations }));
        }
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, onStatsLoaded]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchGroupFiles = useCallback(async (dateStr) => {
    if (loadedFilesRef.current[dateStr]) return;
    setLoadingFiles(prev => new Set(prev).add(dateStr));
    try {
      const token = getAccessToken();
      const params = { groupType: 'date', groupId: dateStr };
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get(`${API_BASE_URL}/user-files/group-files`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      if (response.data.success) {
        setLoadedFiles(prev => ({ ...prev, [dateStr]: response.data.data.files }));
        if (response.data.data.master_coverage) {
          setMasterCoverage(prev => ({ ...prev, ...response.data.data.master_coverage }));
        }
      }
    } catch (err) {
      console.error('Failed to load files for date:', dateStr, err);
    } finally {
      setLoadingFiles(prev => {
        const s = new Set(prev);
        s.delete(dateStr);
        return s;
      });
    }
  }, [searchTerm]);

  const handleFileUpdate = useCallback((update) => {
    setLoadedFiles(prev => {
      // Track changes via ref so the map() callback below doesn't trip
      // eslint no-loop-func (closing over a let in a for loop).
      const flag = { updated: false };
      const next = {};
      for (const [key, files] of Object.entries(prev)) {
        const updatedFiles = files.map(file => {
          const fileId = file.file_id || file._id;
          if (fileId === update.file_id) {
            flag.updated = true;
            return { ...file, plate_solve_status: update.plate_solve_status, plate_solve_result: update.plate_solve_result || file.plate_solve_result };
          }
          return file;
        });
        next[key] = updatedFiles;
      }
      return flag.updated ? next : prev;
    });
  }, []);

  useFileWebSocket(handleFileUpdate);

  const handleSolveFile = async (fileId) => {
    try {
      const token = getAccessToken();
      await axios.post(`${API_BASE_URL}/user-files/${fileId}/plate-solve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { alert('Failed to queue plate solving job'); }
  };

  const toggleDate = (dateStr) => {
    setExpandedDates(prev => {
      const s = new Set(prev);
      if (s.has(dateStr)) {
        s.delete(dateStr);
      } else {
        s.add(dateStr);
        fetchGroupFiles(dateStr);
      }
      return s;
    });
  };

  const toggleSet = (setter, key) => {
    setter(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data?.groups?.length) {
    return <Typography color="textSecondary" sx={{ textAlign: 'center', p: 3 }}>No files found</Typography>;
  }

  return (
    <Stack spacing={2}>
      {selectionEnabled && (
        <SelectionBar
          count={selection.size}
          onClear={selection.clear}
          onDelete={() => onBulkDelete(selection.getSelectedFileIds())}
        />
      )}
      {data.groups.map((group) => {
        const groupId = group._id;
        const files = loadedFiles[groupId];
        const isLoadingFiles = loadingFiles.has(groupId);
        const targetGroups = files ? groupFilesByTarget(files) : {};
        // Ordered, selectable target rows for this date (range scope is confined
        // to this date). A row is selectable only if the user may delete all of
        // its files.
        const sortedTargets = Object.entries(targetGroups).sort(([a], [b]) => a.localeCompare(b));
        const selectableTargets = sortedTargets.filter(([, targetFiles]) =>
          !canDelete || targetFiles.every((f) => canDelete(f)));
        const orderedKeys = selectableTargets.map(([targetName]) => `${groupId}-${targetName}`);
        const selectableIndex = new Map(orderedKeys.map((k, i) => [k, i]));
        const fileIdsByKey = Object.fromEntries(
          selectableTargets.map(([targetName, targetFiles]) => [`${groupId}-${targetName}`, fileIdsOf(targetFiles)]));

        return (
          <Accordion
            key={groupId}
            expanded={expandedDates.has(groupId)}
            onChange={() => toggleDate(groupId)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                <CalendarIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6">{group._id}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={`${group.target_count || 0} targets`} size="small" />
                  <Chip label={`${group.observation_count} obs`} size="small" />
                  <Chip label={`${group.total_files} files`} size="small" />
                  <Chip label={formatSize(group.total_size)} size="small" variant="outlined" />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => { e.stopPropagation(); onDownloadGroup('date', group._id, group._id); }}
                    title={`Download all files from ${group._id}`}
                  >
                    <DownloadIcon />
                  </IconButton>
                  {onPushGroup && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); onPushGroup('date', group._id, group._id); }}
                      title={`Push all files from ${group._id} to external storage`}
                    >
                      <PushIcon />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {isLoadingFiles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !files ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} align="center">
                  No files for this date
                </Typography>
              ) : Object.keys(targetGroups).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} align="center">
                  No files for this date
                </Typography>
              ) : (
                <Box>
                  {sortedTargets.map(([targetName, targetFiles]) => {
                    const targetKey = `${groupId}-${targetName}`;
                    const selectable = selectionEnabled && selectableIndex.has(targetKey);
                    return (
                      <Accordion
                        key={targetKey}
                        expanded={expandedTargets.has(targetKey)}
                        // Shift/Ctrl/Cmd-click selects the row; a plain click
                        // expands it as usual.
                        onChange={(e) => {
                          if (selectable && (e.shiftKey || e.ctrlKey || e.metaKey)) {
                            selection.handleClick(groupId, selectableIndex.get(targetKey), orderedKeys, fileIdsByKey, e);
                          } else {
                            toggleSet(setExpandedTargets, targetKey);
                          }
                        }}
                        disableGutters
                        // Lazy-mount the image grid: expanding a date should only
                        // render target rows, not every file's preview card at
                        // once (memory blow-up on dates with many files).
                        TransitionProps={{ unmountOnExit: true }}
                        sx={{ ...nestedAccordionSx, ...(selectable && selection.isSelected(targetKey) && { bgcolor: 'action.selected' }) }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ pl: 4, userSelect: 'none' }}
                          title={selectable ? 'Click to expand · Shift/Ctrl-click to select' : undefined}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                              <TargetIcon sx={{ mr: 1, fontSize: 20 }} color="action" />
                              <Typography variant="subtitle1">{targetName}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Chip label={`${targetFiles.length} files`} size="small" />
                              <Chip label={formatSize(sumFileBytes(targetFiles))} size="small" variant="outlined" />
                            </Stack>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0, pl: 4 }}>
                          <FileGrid
                            files={targetFiles}
                            onPreview={onPreview}
                            onSolve={handleSolveFile}
                            getDownloadUrl={getDownloadUrl}
                            getCalibratedDownloadUrl={getCalibratedDownloadUrl}
                            downloadCalibrated={downloadCalibrated}
                            onCalibrateError={onCalibrateError}
                            masterCoverage={masterCoverage}
                            onDelete={onDelete}
                            canDelete={canDelete}
                          />
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default DateGroupList;
